import React, { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/auth";
import FileUploader from "@/components/FileUploader";
import { toast } from "sonner";

const SITUATIONS = [
  { key: "specs", label: "Il m'a transmis des spécifications à respecter", hint: "format, fonds perdus, CMJN, PDF/X, résolution…" },
  { key: "refused", label: "Il a signalé un problème ou refusé mon fichier" },
  { key: "unclear", label: "Je ne comprends pas exactement ce qu'il demande" },
  { key: "check", label: "Je souhaite simplement être sûr que mon fichier correspond à ses consignes" },
];

const INCLUDED = [
  "Analyse du fichier", "Lecture des consignes", "Corrections techniques nécessaires",
  "Fonds perdus lorsque nécessaires", "Paramètres d'impression", "Export final",
  "Contrôle", "Fichier conforme prêt à être envoyé",
];

export default function ComplianceConfigurator() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initial = params.get("situation") || "specs";
  const [situation, setSituation] = useState(initial);
  const [files, setFiles] = useState([]);
  const [hasSource, setHasSource] = useState("unknown");
  const [sourceFiles, setSourceFiles] = useState([]);
  const [printerMessage, setPrinterMessage] = useState("");
  const [specsFiles, setSpecsFiles] = useState([]);
  const [printerName, setPrinterName] = useState("");
  const [printerUrl, setPrinterUrl] = useState("");
  const [widthMm, setWidthMm] = useState("");
  const [heightMm, setHeightMm] = useState("");
  const [formatRequested, setFormatRequested] = useState("");
  const [rv, setRv] = useState(null);
  const [notes, setNotes] = useState("");
  const [confirmChk, setConfirmChk] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fileIds = useMemo(() => files.map((f) => f.id).filter(Boolean), [files]);
  const sourceFileIds = useMemo(() => sourceFiles.map((f) => f.id).filter(Boolean), [sourceFiles]);
  const specsFileIds = useMemo(() => specsFiles.map((f) => f.id).filter(Boolean), [specsFiles]);

  const submit = async () => {
    if (!fileIds.length) {
      toast.error("Veuillez joindre le fichier à corriger avant de confirmer votre demande.");
      return;
    }
    if (!confirmChk) { toast.error("Cochez la confirmation pour envoyer votre demande."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/compliance/order", {
        situation, printer_message: printerMessage.trim(), printer_name: printerName.trim(), printer_url: printerUrl.trim(),
        has_source: hasSource,
        width_mm: widthMm ? Number(widthMm) : null, height_mm: heightMm ? Number(heightMm) : null,
        recto_verso: rv, format_requested: formatRequested.trim(), extra_info: "", notes: notes.trim(),
        file_ids: fileIds, source_file_ids: sourceFileIds, specs_file_ids: specsFileIds, confirm: true,
      });
      toast.success("Votre demande est bien enregistrée.");
      nav(`/espace/commande/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="cs-cream min-h-[85vh] py-14" data-testid="page-compliance">
      <div className="max-w-[1000px] mx-auto px-6 lg:px-12">
        <Link to="/preparer-mon-projet" className="text-sm font-mono uppercase tracking-[0.16em] text-neutral-500 hover:text-black inline-flex items-center gap-2">
          <ArrowLeft size={14} /> Retour
        </Link>
        <div className="mt-4">
          <div className="cs-eyebrow text-[#0E7C90]">Prépresse · Configurateur</div>
          <h1 className="cs-h1 mt-2 text-4xl lg:text-5xl">Rendre mon fichier conforme à l'imprimeur</h1>
          <p className="mt-4 text-neutral-700 max-w-2xl text-[15px] leading-relaxed">
            Envoyez-nous votre fichier et les indications reçues. Charbonier Studio l'analyse, effectue les corrections
            nécessaires et vous livre un fichier prêt à être renvoyé. Tarif unique <strong>29 €</strong> — aucun supplément sans votre accord.
          </p>
        </div>

        <div className="mt-8 grid lg:grid-cols-[1fr_300px] gap-8">
          <div className="space-y-6">
            <section className="cs-card p-6">
              <div className="cs-eyebrow text-neutral-500">Étape 1 · Votre situation</div>
              <h2 className="cs-h3 mt-2 text-xl">Que vous demande votre imprimeur ?</h2>
              <div className="mt-4 grid gap-3">
                {SITUATIONS.map((s) => (
                  <button key={s.key} type="button" onClick={() => setSituation(s.key)} data-testid={`situation-${s.key}`}
                    className={`text-left px-4 py-4 border ${situation === s.key ? "border-[#0E7C90] bg-[#0E7C90]/10" : "border-black/20 bg-white"}`}>
                    <div className="font-serif text-[15px]">{s.label}</div>
                    {s.hint && <div className="text-[12px] text-neutral-500 mt-1">{s.hint}</div>}
                  </button>
                ))}
              </div>
            </section>

            <section className="cs-card p-6 space-y-4">
              <div className="cs-eyebrow text-neutral-500">Étape 2 · Votre fichier</div>
              <p className="text-[13px] text-neutral-600">Envoyez le fichier que vous souhaitez transmettre à votre imprimeur ou celui qui a été refusé. <strong className="text-black">Obligatoire.</strong></p>
              <FileUploader files={files} setFiles={setFiles} testId="compliance-file" />
              {!fileIds.length && (
                <div role="alert" aria-live="polite" className="text-[13px] text-red-700 border border-red-400 bg-red-50 px-3 py-2" data-testid="compliance-file-missing">
                  Veuillez joindre le fichier à corriger avant de confirmer votre demande.
                </div>
              )}
              <div className="pt-2">
                <div className="cs-eyebrow text-neutral-500">Disposez-vous du fichier source modifiable ?</div>
                <div className="mt-2 flex gap-2">
                  {[{k:"yes",l:"Oui"},{k:"no",l:"Non"},{k:"unknown",l:"Je ne sais pas"}].map((o) => (
                    <button key={o.k} type="button" onClick={() => setHasSource(o.k)} data-testid={`source-${o.k}`}
                      className={`px-4 py-2 border text-[13px] ${hasSource === o.k ? "border-[#0E7C90] bg-[#0E7C90]/10" : "border-black/20 bg-white"}`}>{o.l}</button>
                  ))}
                </div>
                {hasSource === "yes" && (
                  <div className="mt-3">
                    <FileUploader files={sourceFiles} setFiles={setSourceFiles} testId="compliance-source" />
                  </div>
                )}
              </div>
            </section>

            <section className="cs-card p-6 space-y-4">
              <div className="cs-eyebrow text-neutral-500">Étape 3 · Consignes de l'imprimeur</div>
              <label className="block">
                <span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Copier/coller le message reçu</span>
                <textarea rows={5} value={printerMessage} onChange={(e) => setPrinterMessage(e.target.value)} className="cs-input mt-2 w-full resize-none" data-testid="compliance-message"
                  placeholder="Ex. : votre fichier ne possède pas 3 mm de fond perdu et doit être fourni en PDF/X-4 CMJN…" />
              </label>
              <div>
                <span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Ou joindre une capture / un PDF</span>
                <div className="mt-2"><FileUploader files={specsFiles} setFiles={setSpecsFiles} testId="compliance-specs" category="printer_specs" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <label><span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Imprimeur (facultatif)</span>
                  <input value={printerName} onChange={(e) => setPrinterName(e.target.value)} className="cs-input mt-2 w-full" data-testid="compliance-printer" placeholder="Ex. Exaprint, Onlineprinters, KDP…" /></label>
                <label><span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Fiche technique (URL, facultatif)</span>
                  <input value={printerUrl} onChange={(e) => setPrinterUrl(e.target.value)} className="cs-input mt-2 w-full" data-testid="compliance-url" placeholder="https://…" /></label>
              </div>
            </section>

            <section className="cs-card p-6 space-y-4">
              <div className="cs-eyebrow text-neutral-500">Étape 4 · Informations complémentaires</div>
              <p className="text-[12px] text-neutral-500">Toutes les informations ci-dessous sont facultatives.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <label><span className="text-[11px] font-mono text-neutral-500">Largeur (mm)</span>
                  <input type="number" value={widthMm} onChange={(e) => setWidthMm(e.target.value)} className="cs-input mt-1 w-full" data-testid="compliance-w" /></label>
                <label><span className="text-[11px] font-mono text-neutral-500">Hauteur (mm)</span>
                  <input type="number" value={heightMm} onChange={(e) => setHeightMm(e.target.value)} className="cs-input mt-1 w-full" data-testid="compliance-h" /></label>
              </div>
              <label className="block"><span className="text-[11px] font-mono text-neutral-500">Format demandé</span>
                <input value={formatRequested} onChange={(e) => setFormatRequested(e.target.value)} className="cs-input mt-1 w-full" data-testid="compliance-format" placeholder="Ex. A5, DL, 148 × 210 mm…" /></label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setRv(false)} className={`px-3 py-2 border text-[13px] ${rv === false ? "border-[#0E7C90] bg-[#0E7C90]/10" : "border-black/20 bg-white"}`}>Recto uniquement</button>
                <button type="button" onClick={() => setRv(true)} className={`px-3 py-2 border text-[13px] ${rv === true ? "border-[#0E7C90] bg-[#0E7C90]/10" : "border-black/20 bg-white"}`}>Recto-verso</button>
                <button type="button" onClick={() => setRv(null)} className={`px-3 py-2 border text-[13px] ${rv === null ? "border-[#0E7C90] bg-[#0E7C90]/10" : "border-black/20 bg-white"}`}>Je ne sais pas</button>
              </div>
              <label className="block"><span className="text-[11px] font-mono text-neutral-500">Une précision à nous transmettre ?</span>
                <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="cs-input mt-1 w-full resize-none" data-testid="compliance-notes" placeholder="Ex. l'imprimeur doit recevoir le fichier demain." /></label>
            </section>

            <section className="cs-card p-6 space-y-3">
              <div className="cs-eyebrow text-neutral-500">Étape 5 · Récapitulatif</div>
              <div className="text-sm">
                <div><span className="text-neutral-500 font-mono text-[11px] uppercase">Situation</span><br />{SITUATIONS.find((s) => s.key === situation)?.label}</div>
                <div className="mt-2"><span className="text-neutral-500 font-mono text-[11px] uppercase">Fichiers</span><br />{files.length + specsFiles.length + sourceFiles.length} fichier(s) joint(s)</div>
              </div>
              <ul className="text-[13px] space-y-1">
                {INCLUDED.map((x, i) => <li key={i} className="flex items-start gap-2"><Check size={14} className="text-[#0E7C90] mt-0.5" />{x}</li>)}
              </ul>
              <p className="text-[12px] font-mono text-neutral-500">Aucun supplément sans votre accord.</p>
            </section>

            <section className="cs-card p-6 space-y-3">
              <div className="cs-eyebrow text-neutral-500">Étape 6 · Confirmation</div>
              <label className="flex items-start gap-3 text-[13px]">
                <input type="checkbox" checked={confirmChk} onChange={(e) => setConfirmChk(e.target.checked)} data-testid="compliance-confirm" className="mt-1" />
                <span>Je confirme que les fichiers et informations transmis correspondent à ma demande.</span>
              </label>
              <button onClick={submit} disabled={!confirmChk || submitting || !fileIds.length} data-testid="compliance-submit"
                className="cs-btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
                {submitting ? "Envoi…" : "Confirmer ma demande — 29 €"}
              </button>
            </section>
          </div>

          <aside className="lg:sticky lg:top-4 h-fit">
            <div className="cs-card p-5">
              <div className="cs-eyebrow text-neutral-500">Tarif</div>
              <div className="mt-3 font-serif text-4xl">29 €</div>
              <p className="mt-3 text-[13px] text-neutral-600 leading-relaxed">Tarif unique — fonds perdus et préparation technique inclus lorsqu'ils sont nécessaires. Aucun supplément sans votre accord.</p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
